import { CREATE_ACCESS_GROUP } from '@/common/actions/create';
import { getAccessGroupPath, groupRoleSelectOptions } from '../../store';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Input,
  Modal,
  SelectSingle,
  TextArea,
  TextField,
} from '@servicepipe/ui-components';
import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const FORM_ID = 'create-access-group-form';

const validationSchema = Yup.object({
  role: Yup.object({
    value: Yup.string().oneOf(['ENGINEER', 'READ_ONLY']).required('The field is required'),
    label: Yup.string().required(),
  })
    .nullable()
    .required('The field is required'),
  name: Yup.string()
    .required('The field is required')
    .max(50, 'Group name must be shorter than 50 symbols'),
  description: Yup.string().max(300, 'Description must be shorter than 300 symbols').optional(),
});

const defaultValues = {
  role: null,
  name: '',
  description: '',
};

export const ModalCreateAccessGroup = ({ isShow, onClose, onCreated }) => {
  const { id: partnerClientAccountId } = useParams();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const name = watch('name');
  const description = watch('description');

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
  }, [onClose, reset]);

  const [createAccessGroup, { loading }] = useMutation(CREATE_ACCESS_GROUP, {
    onCompleted: (data) => {
      const groupId = data.createAccessGroup?.id;
      onCreated?.();
      handleClose();
      toast.success('Success');

      if (groupId && partnerClientAccountId) {
        navigate(getAccessGroupPath(partnerClientAccountId, groupId));
      }
    },
    onError: (error) => toast.error(error.message),
  });

  if (!isShow) {
    return null;
  }

  return (
    <Modal
      enableMaximize={false}
      size="m"
      title="Create group"
      toggle={handleClose}
      closeOnClickOutside={!Boolean(name?.trim() || description?.trim())}
      footer={
        <Box display="flex" justify="flex-end" gap="8px" width="100%" padding="0 8px">
          <Button
            variant="outline"
            color="neutral"
            type="button"
            isDisabled={loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="accent"
            type="submit"
            form={FORM_ID}
            isDisabled={loading}
            isLoading={loading}
          >
            Create
          </Button>
        </Box>
      }
    >
      <form
        id={FORM_ID}
        style={{ width: '100%' }}
        onSubmit={handleSubmit((formData) =>
          createAccessGroup({
            variables: {
              input: {
                name: formData.name.trim(),
                role: formData.role.value,
                partnerClientAccountId: Number(partnerClientAccountId),
                description: formData.description?.trim() ? formData.description.trim() : undefined,
              },
            },
          })
        )}
      >
        <Box direction="column" gap="16px" width="100%">
          <Controller
            name="role"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                id="role"
                label="Role"
                isError={Boolean(errors.role)}
                helperText={errors.role?.message || errors.role?.value?.message || ''}
              >
                <SelectSingle
                  value={value}
                  onChange={onChange}
                  placeholder="Select role"
                  options={groupRoleSelectOptions}
                />
              </TextField>
            )}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                id="name"
                label="Group name"
                isError={Boolean(errors.name)}
                helperText={errors.name?.message || ''}
              >
                <Input width="100%" onChange={onChange} value={value} placeholder="Company team" />
              </TextField>
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                id="description"
                label="Description"
                isError={Boolean(errors.description)}
                helperText={errors.description?.message || ''}
              >
                <TextArea
                  width="100%"
                  onChange={onChange}
                  value={value}
                  placeholder="Briefly describe the purpose of this group"
                />
              </TextField>
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
